import React, { useState, useEffect, useRef } from "react";
import {
    IconButton,
    Popover,
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Badge,
    Drawer,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { ChevronRight, ChevronLeft } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EMPLOYER_ROLE, ADMIN_ROLE, EMPLOYEE_ROLE, CS_TECHNICAL_ROLE, CS_OTHERS_ROLE } from 'config';


// Import your custom API hook and service
import useAPI from "hooks/useAPI"; // <-- Adjust the import path
import EmployeeService from "services/EmployeeService"; // <-- Adjust the import path
import useAuth from 'hooks/useAuth';
import NotificationList from "jsx/pages/Notifications/Notiffication";


/**
 * Props:
 *   - company_uuid: The company's unique identifier
 *   - employee_uuid: The employee's unique identifier
 *   - page: The page number for pagination (default is 1)
 *   - limit: The maximum number of notifications per API call (default is 15)
 * 
 */

const NotificationModule = ({ company_uuid, employee_uuid, page = 1, limit = 15 }) => {

    const { isLoggedIn, user } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [hasUnread, setHasUnread] = useState(false);
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [reload, setReload] = useState(false);


    const handleClose = () => {
        setAnchorEl(null);
    };

    const toggleDrawer = (open) => () => {
        handleClose();
        setDrawerOpen(open);
    };

    // useRef to track the previous notifications count
    const prevNotificationsLength = useRef(notifications.length);

    /**
     * Callback to handle the API response.
     * This callback will be called by the custom useAPI hook.
     */
    const handleNotifiList = (response) => {
        // console.log(response.data.items);
        // Extract new notifications from the response
        const newNotifications = response.data.items || [];

        // Display a toast for the new notifications, if any
        // if (newNotifications.length > 0) {
        //     try {
        //         console.log('newNotifications', newNotifications.length);
        //         newNotifications.forEach((notif) => {
        //             // Use the notification title in the toast message
        //             toast.info(notif.title, {
        //                 position: "bottom-left",
        //             });
        //         });
        //     } catch (err) {
        //         console.error("Error displaying toast:", err);
        //     }

        // }

        // Update the notifications state by appending the new notifications
        setNotifications((prev) => [...prev, ...newNotifications]);

        // If any of the new notifications are unread, update the badge
        if (newNotifications.some((notif) => notif.readStatus !== 1)) {
            setHasUnread(true);
        }
    };

    // Initialize the API hook with the EmployeeService.notificationList endpoint and the callback.
    const notiList = useAPI(EmployeeService.UnreadList, handleNotifiList);

    // Execute the API call whenever company_uuid, employee_uuid, page, or limit change.
    useEffect(() => {
        if (user && user.role !== CS_TECHNICAL_ROLE && user.role !== CS_OTHERS_ROLE) {
            notiList.execute(company_uuid, employee_uuid, 1, 5);
        }
    }, [company_uuid, employee_uuid, page, limit]);

    // (Optional) Additional useEffect if you want to track changes in notifications state.
    // Here, we're using a ref to detect an increase in the notifications count.
    useEffect(() => {
        if (notifications.length > prevNotificationsLength.current) {
            // (Optional) You could also toast here, but it's already handled in handleNotifiList.
            // const newCount = notifications.length - prevNotificationsLength.current;
            // toast.info(`You have ${newCount} new notification${newCount > 1 ? "s" : ""}!`, {
            //   position: toast.POSITION.TOP_RIGHT,
            // });
        }
        prevNotificationsLength.current = notifications.length;
    }, [notifications]);

    // Handle clicking the notifications icon: open popover and mark notifications as read
    const handleIconClick = (event) => {
        setAnchorEl(event.currentTarget);
        setHasUnread(false);
    };



    const open = Boolean(anchorEl);
    const id = open ? "notification-popover" : undefined;

    return (
        <div>
            {/* Notification Icon with Badge */}
            <IconButton aria-describedby={id} onClick={handleIconClick}>
                <Badge color="error" variant="dot" invisible={!hasUnread}>
                    <NotificationsIcon sx={{ color: "#3f51b5" }} />
                </Badge>
            </IconButton>

            {/* Notification Pop-over */}
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                PaperProps={{
                    style: { width: "400px" },
                }}
            >
                <Box sx={{ width: 400, p: 2, backgroundColor: "#f0f8ff", borderRadius: 1 }}>
                    <Typography
                        variant="h6"
                        gutterBottom
                        color="#3f51b5"
                        sx={{ fontWeight: "bold", borderBottom: "2px solid blue", pb: 1 }}
                    >
                        Notifications
                    </Typography>
                    <List>
                        {notifications.map((notification) => (
                            <ListItem key={notification.uuid}>
                                {/* Avatar/Icon Section */}
                                <ListItemAvatar>
                                    {notification.image ? (
                                        <Avatar alt={notification.title} src={notification.image} />
                                    ) : (
                                        <Avatar sx={{ backgroundColor: "#3f51b5", color: "white" }}>
                                            {notification.title.charAt(0).toUpperCase()}
                                        </Avatar>
                                    )}
                                </ListItemAvatar>

                                {/* Unread Dot Before Message */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Badge
                                        color="primary"
                                        variant="dot"
                                        invisible={notification.readStatus === 1} // Show dot only if unread
                                        sx={{
                                            "& .MuiBadge-dot": {
                                                width: 6,
                                                height: 8,
                                                borderRadius: "50%",
                                                marginRight: "5px",
                                            },
                                        }}
                                    />
                                    <ListItemText
                                        primary={notification.title}
                                        secondary={notification.description}
                                        primaryTypographyProps={{
                                            fontWeight: notification.readStatus === 1 ? "normal" : "bold",
                                        }}
                                    />
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "primary",
                            color: "white",
                            "&:hover": {
                                backgroundColor: "#0047ab",
                            },
                        }}
                        fullWidth
                        onClick={toggleDrawer(true)}
                    >
                        View All
                    </Button>
                </Box>
            </Popover>
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                PaperProps={{
                    sx: { width: "75vw", maxWidth: "1000px", p: 3, backgroundColor: "#f9f9f9" }, // Set width to 50% of the screen
                }}
            >
                {/* Drawer Header with Close Button */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <IconButton onClick={toggleDrawer(false)}>
                        <ChevronRight fontSize="large" />
                    </IconButton>
                    {/* <Typography variant="h5" fontWeight="bold">Notifications</Typography> */}
                </Box>

                {/* Full Notification UI (Inside Drawer) */}
                <NotificationList reload={reload} setReload={setReload} />
            </Drawer>
            {/* ToastContainer renders the toast notifications */}
            <ToastContainer />
        </div>
    );
};

export default NotificationModule;
